import React from 'react';
import { Box, Typography } from '@mui/material';

export type CounterProps = {
  value: number;
  prefix?: string;
  suffix?: string;
  prefixNewline?: boolean;
  suffixNewline?: boolean;
  bg?: string;
  textColor?: string;
  radius?: number;
  scale?: number;
  variant?: 'classic' | 'radial' | 'glass';
  fontFamily?: string;
  valueFontSize?: number; // px
  prefixFontSize?: number; // px
  suffixFontSize?: number; // px
  gradientFrom?: string;
  gradientTo?: string;
  ringWidth?: number; // px for radial
  shadow?: 'soft' | 'medium' | 'strong' | 'glow' | 'none';
  numberLocale?: string; // e.g. 'de-DE', 'en-US'
};

const Counter: React.FC<CounterProps> = ({
  value,
  prefix = '',
  suffix = '',
  prefixNewline = false,
  suffixNewline = false,
  bg = 'rgba(255,255,255,0.9)',
  textColor = '#111827',
  radius = 8,
  scale = 1,
  variant = 'classic',
  fontFamily,
  valueFontSize = 28,
  prefixFontSize = 12,
  suffixFontSize = 12,
  gradientFrom = '#60a5fa',
  gradientTo = '#22c55e',
  ringWidth = 10,
  shadow = 'soft',
  numberLocale = 'de-DE',
}) => {
  const formattedValue = new Intl.NumberFormat(numberLocale || 'de-DE').format(value);
  const content = (
    <>
      {!!prefix && prefixNewline && (
        <Typography component="div" sx={{ fontWeight: 800, fontSize: prefixFontSize, opacity: .85, mb: .25 }}>{prefix}</Typography>
      )}
      <Typography component="div" sx={{ fontWeight: 900, fontSize: valueFontSize, lineHeight: 1, color: textColor }}>
        {!prefixNewline && !!prefix && (
          <Box component="span" sx={{ mr: 0.5 }}>{prefix}</Box>
        )}
        <Box component="span" sx={{
          background: `linear-gradient(90deg, ${gradientFrom}, ${gradientTo})`,
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          color: 'transparent',
          WebkitTextFillColor: 'transparent',
        }}>
          {formattedValue}
        </Box>
        {!suffixNewline && !!suffix && (
          <Box component="span" sx={{ ml: 0.5 }}>{suffix}</Box>
        )}
      </Typography>
      {!!suffix && suffixNewline && (
        <Typography component="div" sx={{ fontWeight: 800, fontSize: suffixFontSize, opacity: .85, mt: .25 }}>{suffix}</Typography>
      )}
    </>
  );

  const scaled = Math.max(0.5, Math.min(3, Number(scale) || 1));
  const rpx = `${Math.max(0, Math.min(28, Number(radius) || 8))}px`;
  const boxShadow = shadow === 'none' ? 'none'
    : shadow === 'soft' ? '0 10px 24px rgba(0,0,0,0.18)'
    : shadow === 'medium' ? '0 14px 36px rgba(0,0,0,0.22)'
    : shadow === 'strong' ? '0 18px 48px rgba(0,0,0,0.28)'
    : '0 0 0 3px rgba(59,130,246,0.35), 0 18px 48px rgba(59,130,246,0.25)';

  if (variant === 'radial') {
    const size = 160; // base size in px
    const stroke = Math.max(2, Math.min(24, Number(ringWidth) || 10));
    const radiusC = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radiusC;
    // Decorative full ring (we could compute a percentage in future)
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transform: `scale(${scaled})`, transformOrigin: 'center', fontFamily }}>
        <Box sx={{ position: 'relative', width: size, height: size, filter: 'drop-shadow(0 10px 24px rgba(0,0,0,0.15))' }}>
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: 'block' }}>
            <defs>
              <linearGradient id="cnt-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={gradientFrom} />
                <stop offset="100%" stopColor={gradientTo} />
              </linearGradient>
            </defs>
            <circle cx={size/2} cy={size/2} r={radiusC} stroke={bg} strokeOpacity={0.7} strokeWidth={stroke} fill="none" />
            <circle cx={size/2} cy={size/2} r={radiusC} stroke="url(#cnt-grad)" strokeWidth={stroke}
              strokeLinecap="round" fill="none" strokeDasharray={`${circumference}`} strokeDashoffset={0}
              transform={`rotate(-90 ${size/2} ${size/2})`} />
          </svg>
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: textColor, textAlign: 'center' }}>
            <Box>
              {content}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  if (variant === 'glass') {
    return (
      <Box sx={{ display: 'inline-block', transform: `scale(${scaled})`, transformOrigin: 'center', fontFamily }}>
        <Box sx={{
          color: textColor,
          borderRadius: rpx,
          px: 2,
          py: 1.25,
          background: 'linear-gradient(180deg, rgba(255,255,255,0.18), rgba(255,255,255,0.10))',
          border: '1px solid rgba(255,255,255,0.35)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          boxShadow: boxShadow + ', inset 0 0 0 1px rgba(255,255,255,0.05)',
          textShadow: '0 1px 12px rgba(0,0,0,0.12)'
        }}>
          {content}
        </Box>
      </Box>
    );
  }

  // classic
  return (
    <Box sx={{ display: 'inline-block', transform: `scale(${scaled})`, transformOrigin: 'center', fontFamily }}>
      <Box sx={{
        bgcolor: bg,
        color: textColor,
        borderRadius: rpx,
        px: 1.5,
        py: 0.75,
        boxShadow
      }}>
        {content}
      </Box>
    </Box>
  );
};

export default Counter;
